import { CourseFindByName, UserAuthors, courseCategoryList, terminalList, CourseCreate } from './Request';
import type { AdminCourseI, AdminCourseCreateI } from 'types';

export interface AdminCoursesFiltersI {
  name?: string | null;
  category?: string | null;
  author?: string | null;
  terminal?: string | null;
  isDraft?: boolean | '' | null;
}

export interface AdminDropdownsI {
  categories: string[];
  authors: string[];
  terminals: string[];
}

const EMPTY_DROPDOWNS: AdminDropdownsI = { categories: [], authors: [], terminals: [] };

/** Shape crudo que entregan Course_findByName / Course_create. */
interface RawAdminCourseI {
  id: string;
  name?: string | null;
  description?: string | null;
  expirationDate?: string | null;
  updatedAt?: string | null;
  thumbnail?: string | null;
  isDraft?: boolean | null;
  Priority?: { name?: string | null } | null;
  CourseCategory?: { name?: string | null } | null;
  User?: { fullName?: string | null; photo?: string | null } | null;
}

const normalizeFilter = <T>(value: T | '' | null | undefined): T | null =>
  value === '' || value === undefined ? null : (value as T | null);

const toAdminCourse = (course: RawAdminCourseI): AdminCourseI => ({
  id: course.id,
  name: course.name ?? '',
  description: course.description ?? null,
  priority: course.Priority?.name ?? null,
  category: course.CourseCategory?.name ?? null,
  creator: course.User ? { fullName: course.User.fullName ?? null, photo: course.User.photo ?? null } : null,
  // El backend a veces manda '' en vez de null; el card ya espera null
  thumbnailKey: course.thumbnail || null,
  updatedAt: course.updatedAt ?? null,
  isDraft: !!course.isDraft,
  expirationDate: course.expirationDate ?? null,
});

/**
 * Cursos de la página admin vía GraphQL Course_findByName.
 *
 * Contrato de filtros: `''` y `undefined` se normalizan a `null` (sin filtro)
 * en category/author/terminal/isDraft — el hook consumidor ya no tiene que
 * hacerlo. `name` es la excepción: el esquema lo pide como `String!`, así que
 * siempre viaja como string y `''` significa "sin búsqueda".
 *
 * OJO: la capa Request no rechaza en errores de request — graphql() resuelve
 * con `{ error: true, ... }` y el destructure del wrapper lo vuelve
 * `undefined`. Se manejan ambos caminos (rechazo y resolución sin lista).
 */
export const getCourses = async (
  filters: AdminCoursesFiltersI = {}
): Promise<{ data: AdminCourseI[]; error: string | null }> => {
  try {
    const courses = (await CourseFindByName({
      name: filters.name ?? '',
      category: normalizeFilter(filters.category),
      author: normalizeFilter(filters.author),
      terminal: normalizeFilter(filters.terminal),
      isDraft: normalizeFilter(filters.isDraft),
    })) as RawAdminCourseI[] | undefined;

    if (!Array.isArray(courses)) return { data: [], error: 'FETCH_ERROR' };
    return { data: courses.map(toAdminCourse), error: null };
  } catch {
    return { data: [], error: 'FETCH_ERROR' };
  }
};

/**
 * Catálogos para los dropdowns de filtros (categorías, autores, terminales),
 * las tres llamadas en paralelo. Si cualquiera falla (rechazo o resolución
 * sin lista por error normalizado) regresa las tres listas vacías con error.
 */
export const getDropdownsInformation = async (): Promise<{ data: AdminDropdownsI; error: string | null }> => {
  try {
    const [categories, authors, terminals] = (await Promise.all([
      courseCategoryList(),
      UserAuthors(),
      terminalList(),
    ])) as [{ name?: string | null }[], { fullName?: string | null }[], { name?: string | null }[]];

    if (![categories, authors, terminals].every(Array.isArray)) return { data: EMPTY_DROPDOWNS, error: 'FETCH_ERROR' };

    return {
      data: {
        categories: categories.map(category => category?.name || ''),
        authors: authors.map(author => author?.fullName || ''),
        terminals: terminals.map(terminal => terminal?.name || ''),
      },
      error: null,
    };
  } catch {
    return { data: EMPTY_DROPDOWNS, error: 'FETCH_ERROR' };
  }
};

/**
 * Alta de curso vía la mutación Course_create. Regresa el curso creado ya
 * mapeado a AdminCourseI, o `{ data: null, error }` en rechazo, error
 * normalizado (resuelve sin curso) o respuesta malformada (sin id).
 */
export const createCourse = async (
  course: AdminCourseCreateI
): Promise<{ data: AdminCourseI | null; error: string | null }> => {
  try {
    const created = (await CourseCreate(course)) as RawAdminCourseI | undefined;

    if (!created?.id) return { data: null, error: 'FETCH_ERROR' };
    return { data: toAdminCourse(created), error: null };
  } catch {
    return { data: null, error: 'FETCH_ERROR' };
  }
};
